<template>
  <!-- 目标日历：员工月视图 + 当日工作/日志。布局参考原型「日程安排」：左侧主区 + 右侧侧栏 -->
  <div class="calendar-view">
    <list-page>
      <search-condition>
        <template #default>
          <el-select v-model="searchInfo.employeeId" placeholder="请选择员工" filterable @change="loadMonth">
            <template #prefix>员工</template>
            <el-option v-for="e in staffOptions" :key="e.employeeId" :label="staffLabel(e)" :value="e.employeeId"/>
          </el-select>
          <el-date-picker v-model="month" type="month" value-format="YYYY-MM" placeholder="选择月份" @change="loadMonth" style="width: 160px"/>
        </template>
        <template #option>
          <page-button type="read" el-type="primary" @click="loadMonth">查询</page-button>
        </template>
      </search-condition>

      <list-table :show-shadow="true">
        <div class="cal-layout">
          <!-- ============ 主区 ============ -->
          <div class="cal-main">
            <!-- 日历。只做「哪天有什么」的指示，不再罗列任务明细 -->
            <div class="cal-card">
              <div class="cal-head">
                <div class="cal-title">{{ monthLabel }}</div>
                <div class="cal-nav">
                  <span class="nav-btn" title="上月" @click="shiftMonth(-1)">‹</span>
                  <span class="nav-btn" title="回到本月" @click="toThisMonth">今</span>
                  <span class="nav-btn" title="下月" @click="shiftMonth(1)">›</span>
                </div>
              </div>
              <div class="cal-legend">
                <span><i class="dot today"></i>今天</span>
                <span><i class="dot task"></i>有任务</span>
                <span><i class="dot done"></i>全部完成</span>
                <span><i class="dot overdue"></i>往日漏填日志</span>
              </div>
              <div class="cal-grid">
                <div v-for="d in ['日','一','二','三','四','五','六']" :key="d" class="cal-dow">{{ d }}</div>
                <template v-for="(cell, i) in gridCells" :key="i">
                  <div v-if="!cell" class="cal-blank"></div>
                  <div v-else class="cal-day"
                       :class="{today: cell.isToday, selected: cell.date === selectedDate, overdue: cell.overdue}"
                       :title="dayTip(cell)"
                       @click="selectDay(cell.date)">
                    <span class="cal-date">{{ cell.day }}</span>
                    <span v-if="cell.taskCount" class="cal-count" :class="cell.allDone ? 'green' : 'blue'">
                      {{ cell.taskCount }}
                    </span>
                    <!-- 放假/调休：右下角标出节日名或「调休」二字 -->
                    <span v-if="cell.holidayText" class="cal-holiday" :class="cell.holidayType">{{ cell.holidayText }}</span>
                  </div>
                </template>
              </div>
            </div>

            <!--
              当日工作：可编辑（日志 + 任务勾选/状态/备注/工时）。
              不再弹框 —— 点日期即把当天数据加载到这里。
              高度撑满剩余空间，内容多时在本模块内部滚动，不把整页顶长。
            -->
            <div class="sch-card">
              <div class="sch-card-head">
                <span class="sch-card-title">当日工作 · {{ selectedDateLabel }}</span>
                <span class="sch-card-actions">
                  <!-- 日志是按人记录的，只有查的是自己时才让写 -->
                  <page-button v-if="canWriteLog" size="small" @click="openLogForm">写日志</page-button>
                  <page-button el-type="primary" size="small" type="write" @click="openScheduleForm">+ 新建日程</page-button>
                  <!-- 假期设置：灰色图标，点开可手工调整放假/调休 -->
                  <yq-icon class="holiday-setting" name="element-ui_setting" title="放假/调休设置"
                           @click="openHolidayForm"/>
                </span>
              </div>
              <div class="sch-body">
                <!-- 工作日志：只读展示，点「写日志」在弹框里改 -->
                <div class="log-block">
                  <div class="log-label">
                    工作日志 <span class="desc">（每日一条）</span>
                    <span v-if="currentCalendarInfo.logEmployeeName" class="desc">
                      · {{ currentCalendarInfo.logEmployeeName }}
                    </span>
                  </div>
                  <div v-if="currentCalendarInfo.result" class="log-text">{{ currentCalendarInfo.result }}</div>
                  <div v-else class="log-empty">当天还没有工作日志</div>
                </div>

                <!--
                  当日目标：两行排版。
                  第一行 分类 + 名称 + 删除/编辑
                  第二行 状态下拉 / 备注 / 工时
                  ★ 改动即保存，不再依赖「写日志」按钮统一提交。
                  ★ 状态用下拉而不是勾选框：一个是非只有「完成/未完成」两态，
                    待办 / 未完成·延期 / 已完成是三态，勾选框表达不了。
                -->
                <div class="task-block">
                  <div class="task-label">
                    目标（{{ currentCalendarInfo.calendarList.length }} 条）
                    <!-- 看别人的日历时，这里整体只读 -->
                    <span v-if="!canEdit" class="readonly-hint">只读（非本人数据）</span>
                  </div>
                  <div v-if="!currentCalendarInfo.calendarList.length" class="sch-empty">当天没有目标</div>
                  <div v-else class="task-list">
                    <div v-for="item in currentCalendarInfo.calendarList" :key="item.id"
                         class="task-row" :class="{'task-row-readonly': !canEdit, 'task-row-done': item.kpiStatus === 100}">
                      <div class="task-line">
                        <!-- 分类：日程与 KPI 任务混在一天里，标出各自是什么 -->
                        <span class="type-tag" :class="'type-' + (item.calendarType || 0)">
                          {{ typeName(item.calendarType) }}
                        </span>
                        <span class="task-name" :title="item.title">{{ item.title }}</span>
                        <!-- 备注：跟在名称后面，单行省略，长了靠 title 悬浮看全 -->
                        <span v-if="item.remark" class="task-remark" :title="item.remark">{{ item.remark }}</span>
                        <!--
                          操作图标固定在行尾。
                          ★ 必须包一层并靠 margin-left:auto 顶到最右，不能直接跟在备注后面：
                            没有备注时它们会贴着名称左移，有备注时又被推到中间，
                            同一列的行图标位置不齐，看着很乱。
                        -->
                        <span class="task-actions" v-if="canEdit">
                          <!-- 日程可以删；KPI 任务要在「编辑」里改，不在这里删 -->
                          <yq-icon v-if="isSchedule(item)" class="task-del" name="element-ui_delete"
                                   title="删除日程" @click.stop="deleteSchedule(item)"/>
                          <!-- 编辑这条日历任务（名称与备注）。只要有 id 就能改 -->
                          <yq-icon v-if="item.id" class="kpi-edit" title="编辑日历任务"
                                   name="element-ui_edit" @click.stop="openRowEdit(item)"/>
                        </span>
                      </div>
                      <div class="task-line">
                        <!--
                          状态：待办 / 未完成·延期 / 已完成。
                          ★ 已完成是终态，下拉禁用 —— 但备注与工时仍可改，
                            因为完成之后补记工时、补写情况说明都是常态。
                          选「未完成·延期」要把任务整体顺延，所以走二次确认。
                        -->
                        <el-select :model-value="item.kpiStatus" size="small" class="status-select"
                                   :disabled="!canEdit || item.kpiStatus === 100"
                                   @change="v => onStatusChange(item, v)">
                          <el-option label="待办" :value="0"/>
                          <el-option label="未完成/延期" :value="50"/>
                          <el-option label="已完成" :value="100"/>
                        </el-select>
                        <el-input v-model="item.result" size="small" class="task-result"
                                  :disabled="!canEdit" placeholder="完成情况备注" @change="onFieldChange(item)"/>
                        <div class="kpi-hours">
                          <span class="hours-label">消耗工时(h)</span>
                          <el-input-number v-model="item.hours" :min="0" :max="9999" :step="1" size="small"
                                           :disabled="!canEdit" controls-position="right" style="width: 120px"
                                           @change="onFieldChange(item)"/>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- ============ 侧栏 ============ -->
          <div class="cal-side">
            <div class="side-card">
              <div class="side-title">目标类型分布</div>
              <div v-show="typeStats.length" ref="typeChart" class="side-chart"></div>
              <div v-if="!typeStats.length" class="sch-empty">本月暂无数据</div>
              <div class="side-legend">
                <span v-for="t in typeStats" :key="t.type" class="legend-row">
                  <i class="dot" :style="{background: t.color}"></i>
                  <span class="legend-name">{{ t.name }}</span>
                  <b class="legend-val">{{ t.value }}</b>
                </span>
              </div>
            </div>

            <div class="side-card">
              <div class="side-title">本周待办</div>
              <div v-if="!weekTodos.length" class="sch-empty">本周没有待办</div>
              <div v-else class="week-list">
                <div v-for="item in weekTodos" :key="item.id" class="week-item" @click="selectDay(dateOnly(item.goalDate))">
                  <span class="week-date">{{ shortDate(item.goalDate) }}</span>
                  <span class="week-title" :title="item.title">{{ item.title }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </list-table>
    </list-page>

    <!--
      新建 KPI任务：填起止日期，保存后跨日自动拆分成每日的日历行。
      ★ 编辑走的是另一个弹框（下面那个），只改日历行自己的名称与备注 ——
        改某一天不该把整条任务的排期重算一遍。
    -->
    <el-dialog v-model="showTaskForm" title="新建任务" dialog-width="1" :close-on-click-modal="false">
      <form-items :data="taskForm" :columns="1" label-width="2.4rem" icon-width="0">
        <form-item label="任务标题" :required="true" name="title" error-message="请填写任务标题" :show-block="true">
          <el-input v-model="taskForm.title" placeholder="如：完成拜访 5 家客户（周一到周五每天 1 家）" maxlength="80"/>
        </form-item>
        <form-item label="开始日期" :required="true" name="startDate" error-message="请选择开始日期">
          <el-date-picker v-model="taskForm.startDate" type="date" value-format="YYYY-MM-DD" placeholder="开始日期" style="width: 100%"/>
        </form-item>
        <form-item label="结束日期" :required="true" name="endDate" error-message="请选择结束日期">
          <el-date-picker v-model="taskForm.endDate" type="date" value-format="YYYY-MM-DD" placeholder="结束日期" style="width: 100%"/>
        </form-item>
        <form-item label="备注" :show-block="true">
          <el-input v-model="taskForm.remark" type="textarea" :rows="2" placeholder="每天怎么拆分的说明，将写入每日 kpi日历的备注"/>
        </form-item>
      </form-items>
      <form-page :show-page="false" :show-border="false" :show-shadow="false">
        <template #left>
          <el-checkbox v-model="taskForm.shiftOtherTask">顺延冲突任务</el-checkbox>
        </template>
        <page-button @click="showTaskForm = false">取消</page-button>
        <page-button type="write" el-type="primary" @click="saveTask">保存并拆分</page-button>
      </form-page>
    </el-dialog>

    <!--
      编辑日历任务：只改这一天的名称与备注。
      可改范围刻意收窄 —— 状态与工时在列表行上直接改，日期与类型属于所属任务的定义，
      想改整条任务请删掉重建。
    -->
    <el-dialog v-model="showRowForm" title="编辑日历任务" dialog-width="1" :close-on-click-modal="false">
      <form-items :data="rowForm" :columns="1" label-width="2.4rem" icon-width="0">
        <form-item label="名称" :required="true" name="title" error-message="请填写名称" :show-block="true">
          <el-input v-model="rowForm.title" placeholder="如：完成拜访 5 家客户" maxlength="80"/>
        </form-item>
        <form-item label="备注" :show-block="true">
          <el-input v-model="rowForm.remark" type="textarea" :rows="3" placeholder="这条任务的补充说明"/>
        </form-item>
      </form-items>
      <form-page :show-page="false" :show-border="false" :show-shadow="false">
        <page-button @click="showRowForm = false">取消</page-button>
        <page-button type="write" el-type="primary" @click="saveRowEdit">保存</page-button>
      </form-page>
    </el-dialog>

    <!-- 新建日程：只落一行，不拆分。与「新建任务」是两回事 -->
    <el-dialog v-model="showScheduleForm" title="新建日程" dialog-width="1" :close-on-click-modal="false">
      <form-items :data="scheduleForm" :columns="1" label-width="2.4rem" icon-width="0">
        <form-item label="日程类型" :required="true" name="calendarType" error-message="请选择日程类型">
          <el-select v-model="scheduleForm.calendarType" placeholder="请选择" style="width: 100%" @change="onScheduleTypeChange">
            <el-option v-for="t in SCHEDULE_TYPE_OPTIONS" :key="t.value" :label="t.label" :value="t.value"/>
          </el-select>
        </form-item>
        <!-- 只有「拜访客户」才需要选客户 -->
        <form-item v-if="scheduleForm.calendarType === 4" label="拜访客户" :required="true"
                   name="partnerId" error-message="请选择要拜访的客户">
          <customer-select v-model="scheduleForm.partnerId" placeholder="请选择客户" style="width: 100%"/>
        </form-item>
        <!-- 「商机客户」与拜访客户一样要挂到具体对象上，只是这里选的是商机 -->
        <form-item v-if="scheduleForm.calendarType === 8" label="商机客户" :required="true"
                   name="partnerId" error-message="请选择商机客户">
          <opportunity-select v-model="scheduleForm.partnerId" placeholder="请选择商机" style="width: 100%"/>
        </form-item>
        <form-item label="日程标题" :required="true" name="title" error-message="请填写日程标题" :show-block="true">
          <el-input v-model="scheduleForm.title" placeholder="如：拜访深圳恒昌科技，沟通年度续约" maxlength="80"/>
        </form-item>
        <form-item label="日程日期" :required="true" name="goalDate" error-message="请选择日程日期">
          <el-date-picker v-model="scheduleForm.goalDate" type="date" value-format="YYYY-MM-DD"
                          placeholder="日程日期" style="width: 100%"/>
        </form-item>
        <form-item label="备注" :show-block="true">
          <el-input v-model="scheduleForm.remark" type="textarea" :rows="2" placeholder="补充说明，选填"/>
        </form-item>
      </form-items>
      <form-page :show-page="false" :show-border="false" :show-shadow="false">
        <page-button @click="showScheduleForm = false">取消</page-button>
        <page-button type="write" el-type="primary" @click="saveSchedule">保存</page-button>
      </form-page>
    </el-dialog>

    <!-- 写工作日志：每个日期一条。下方列出当天已完成/记了工时的目标，可一键拼装成文本 -->
    <el-dialog v-model="showLogForm" :title="'写工作日志 · ' + selectedDateLabel" dialog-width="2" :close-on-click-modal="false">
      <el-input v-model="logForm.result" type="textarea" :rows="6"
                placeholder="记录今天完成的工作内容、遇到的问题、明天的计划"/>

      <div class="log-suggest">
        <div class="log-suggest-title">当天已完成 / 记了工时的目标</div>
        <div v-if="!logSuggestRows.length" class="sch-empty">没有可拼装的目标</div>
        <div v-else class="log-suggest-list">
          <div v-for="r in logSuggestRows" :key="r.id" class="log-suggest-row">
            <span class="type-tag" :class="'type-' + (r.calendarType || 0)">{{ typeName(r.calendarType) }}</span>
            <span class="sug-title" :title="r.title">{{ r.title }}</span>
            <span v-if="r.kpiStatus === 100" class="status-done">已完成</span>
            <span v-if="Number(r.hours) > 0" class="sug-hours">消耗工时 {{ r.hours }}h</span>
          </div>
        </div>
      </div>

      <form-page :show-page="false" :show-border="false" :show-shadow="false">
        <page-button @click="showLogForm = false">取消</page-button>
        <page-button @click="assembleLog">拼装日志</page-button>
        <page-button type="write" el-type="primary" @click="saveLog">保存</page-button>
      </form-page>
    </el-dialog>

    <!--
      放假/调休设置：列出该年已有安排，可改可删；也可新增一天。
      改的是商户自己的数据，不影响别家 —— 表里带 merchant_id。
    -->
    <el-dialog v-model="showHolidayForm" :title="holidayYear + ' 年放假/调休设置'" dialog-width="1" :close-on-click-modal="false">
      <form-items label-width="2.4rem" icon-width="0" :columns="1">
        <form-item label="数据类型">
          <el-select v-model="holidayForm.dayType" placeholder="类型">
            <el-option label="放假" :value="1"/>
            <el-option label="调休上班" :value="2"/>
          </el-select>
        </form-item>
        <form-item label="数据名称">
          <el-input v-model="holidayForm.name" placeholder="名称（选填，如 国庆节）"/>
        </form-item>
        <form-item label="起止日期">
          <el-date-picker v-model="holidayRange" type="daterange" value-format="YYYY-MM-DD"
                          range-separator="至" start-placeholder="开始日期" end-placeholder="结束日期" style="width: 100%;box-sizing: border-box;"/>
        </form-item>
      </form-items>
      <!--
        选一个区间，保存时自动把区间内每一天都写进去 —— 放假多是连着好几天，
        一天一天点太碎。也可以只选一天（开始=结束）。
      -->
      <form-page :show-border="false" :show-shadow="false" :show-page="false">
        <template #left><span v-if="holidayRangeDays > 0" class="range-hint">共 {{ holidayRangeDays }} 天</span></template>
        <page-button @click="showHolidayForm = false">关闭</page-button>
        <page-button el-type="primary" @click="saveHolidaySetting">添加 / 覆盖</page-button>
      </form-page>
    </el-dialog>
  </div>
</template>

<script>
import * as echarts from "echarts";
import {markRaw} from "vue";
import ListPage from "@/components/h5/layout/ListPage.vue";
import SearchCondition from "@/components/h5/layout/SearchCondition.vue";
import ListTable from "@/components/h5/layout/ListTable.vue";
import FormItems from "@/components/h5/layout/FormItems.vue";
import FormItem from "@/components/h5/layout/FormItem.vue";
import PageButton from "@/components/h5/button/PageButton.vue";
import {useRoute, useRouter} from "vue-router";
import Util from "@/util/util";
import calendarApi from "@/api/dz-crm/kpi/calendarApi";
import calendarHolidayApi from "@/api/dz-crm/kpi/calendarHolidayApi";
import YqIcon from "@/components/YqIcon.vue";
import FormPage from "@/components/h5/layout/FormPage.vue";
import CustomerSelect from "@/components/h5/partner/CustomerSelect.vue";
import OpportunitySelect from "@/components/h5/partner/OpportunitySelect.vue";

// kpi日历类型：1工作日志 2kpi任务 3开发节点 4拜访客户 5线上会议 6线下会议 7产品学习 8商机客户 99其它
const CALENDAR_TYPES = {
  "1": "工作日志",
  "2": "KPI任务",
  "3": "开发节点",
  "4": "拜访客户",
  "5": "线上会议",
  "6": "线下会议",
  "7": "产品学习",
  "8": "商机客户",
  "99": "其它",
};
/** 工作日志的类型值。日历红字判定、侧栏统计、当日条目都要用到 */
const TYPE_LOG = 1;
/** 拜访客户：日程里要挂客户档案id的类型 */
const TYPE_VISIT = 4;
/** 商机客户：日程里要挂商机档案id的类型。跟进该商机时这条日程会自动置为已完成 */
const TYPE_OPPORTUNITY = 8;
/**
 * 日程的类型值。这几种走 saveSchedule 单独入库，不参与 KPI 任务的拆分与顺延。
 * 「其它」也算日程 —— 它和拜访/会议一样是「某天要做的一件事」，只是没归到具体类别。
 */
const SCHEDULE_TYPES = [4, 5, 6, 7, 8, 99];

/**
 * 类型配色。侧栏环形图与日程条目的色条共用一套，保证「图上看到的色」与「条目那条色」一致。
 * 日志不参与分布统计，所以这里也没有 type=1 的色。
 */
const TYPE_COLORS = {
  "2": "#1677ff",
  "3": "#faad14",
  "4": "#13b8b8",
  "5": "#7a5af8",
  "6": "#22a55a",
  "7": "#eb2f96",
  "8": "#d4380d",
  "99": "#909399",
};

export default {
  setup() {
    return {router: useRouter(), route: useRoute()};
  },
  name: "CalendarView",
  components: {
    FormPage,
    YqIcon,
    CustomerSelect,
    OpportunitySelect,
    FormItems, FormItem, PageButton,
    SearchCondition, ListTable, ListPage
  },
  data() {
    const now = new Date();
    const mm = String(now.getMonth() + 1).padStart(2, "0");
    return {
      searchInfo: {employeeId: ""},
      staffOptions: [],
      month: now.getFullYear() + "-" + mm,
      rows: [], // 当月全部 kpi日历行
      // 当前查看的日期。不是「今天」—— 点某一天就切到那一天，页面加载时默认今天
      selectedDate: "",
      showTaskForm: false,
      taskForm: {},
      // 编辑单条日历任务（只改名称与备注）
      showRowForm: false,
      rowForm: {},
      showScheduleForm: false,
      scheduleForm: {},
      // 写日志弹框
      showLogForm: false,
      logForm: {result: ""},
      // 放假/调休设置。holidayRange 是 [开始, 结束]，保存时展开成逐天
      showHolidayForm: false,
      holidayForm: {dayType: 1, name: ""},
      holidayRange: [],
      // 当年的放假/调休，键为 yyyy-MM-dd，日历格子与延期判定共用
      holidayMap: {},
      // 日程类型下拉的选项，与后端 saveSchedule 的类型白名单一一对应
      SCHEDULE_TYPE_OPTIONS: [
        {value: 4, label: "拜访客户"},
        {value: 5, label: "线上会议"},
        {value: 6, label: "线下会议"},
        {value: 7, label: "产品学习"},
        {value: 8, label: "商机客户"},
        {value: 99, label: "其它"},
      ],
      userInfo: {},
      // 当日可编辑状态：日志文本 + 目标行（与 rows 解耦，避免编辑时污染日历统计）
      currentCalendarInfo: {result: "", logEmployeeName: "", calendarList: []},
      typeChart: null,
    };
  },
  computed: {
    currentStaff() {
      return this.staffOptions.find(e => e.employeeId === this.searchInfo.employeeId) || null;
    },
    monthRows() {
      // 日历格子/当日详情都用当月已返回数据
      return this.rows || [];
    },
    /**
     * 路由上的 date 参数（yyyy-MM-dd），消息推送里跳过来时用它定位到具体某天。
     *
     * ★ 必须验格式：月份、日期都是从这个串上截出来的，
     *   传个乱七八糟的值（或数组）会把月份也带偏，页面直接白屏看不到数据。
     */
    routeDate() {
      const date = this.dateOnly(this.route.query.date);
      return /^\d{4}-\d{2}-\d{2}$/.test(date) ? date : "";
    },
    /** 月标题：2026年 09月 */
    monthLabel() {
      if (!this.month) {
        return "";
      }
      const [y, m] = this.month.split("-");
      return y + "年 " + m + "月";
    },
    /** 当日工作标题里的日期。只到「月-日」，年份在左边的日历标题里已经写了 */
    selectedDateLabel() {
      return this.selectedDate ? this.selectedDate.slice(5) : "";
    },
    /** 假期设置弹框操作的年份：跟着当前查看的月份走，翻到哪年就设哪年 */
    holidayYear() {
      return this.month ? Number(this.month.slice(0, 4)) : new Date().getFullYear();
    },
    /** 该年已有的放假/调休，按日期升序 */
    holidayList() {
      return Object.keys(this.holidayMap)
          .filter(d => d.startsWith(String(this.holidayYear)))
          .sort()
          .map(d => this.holidayMap[d]);
    },
    /**
     * 把选中的日期区间展开成逐天（含首尾）
     *
     * ★ 用本地时间逐天加，不用 toISOString() 做日期运算 —— 那是 UTC，
     *   东八区凌晨会退到前一天，导致区间首尾各错一天。
     */
    holidayRangeDates() {
      if (!this.holidayRange || this.holidayRange.length !== 2) {
        return [];
      }
      const [start, end] = this.holidayRange;
      if (!start || !end || end < start) {
        return [];
      }
      const dates = [];
      const [sy, sm, sd] = start.split("-").map(Number);
      const [ey, em, ed] = end.split("-").map(Number);
      const cur = new Date(sy, sm - 1, sd);
      const last = new Date(ey, em - 1, ed);
      // 上限一年，防止误选了跨多年的区间时把几万条请求打出去
      let guard = 0;
      while (cur <= last && guard < 366) {
        const y = cur.getFullYear();
        const m = String(cur.getMonth() + 1).padStart(2, "0");
        const d = String(cur.getDate()).padStart(2, "0");
        dates.push(y + "-" + m + "-" + d);
        cur.setDate(cur.getDate() + 1);
        guard++;
      }
      return dates;
    },
    holidayRangeDays() {
      return this.holidayRangeDates.length;
    },
    /**
     * 当前看的是不是本人的日历。
     *
     * 页面上可以切到别的员工看他的日历，但那是只读的：
     * 日志按人记录不能替别人写，目标任务也不该替别人改状态/工时/删除。
     */
    isSelf() {
      return !!this.userInfo.id && this.searchInfo.employeeId === this.userInfo.id;
    },
    /** 能不能写日志。与能否编辑目标任务同一个前提 */
    canWriteLog() {
      return this.isSelf;
    },
    /** 能不能编辑目标任务（勾选 / 状态下拉 / 备注 / 工时 / 删除日程 / 编辑任务） */
    canEdit() {
      return this.isSelf;
    },
    /**
     * 写日志弹框里「可拼装」的目标：当天已完成，或记了工时的。
     *
     * 只做提示，不参与保存 —— 保存的是上面那个文本框的内容。
     */
    logSuggestRows() {
      return (this.currentCalendarInfo.calendarList || [])
          .filter(r => r.kpiStatus === 100 || Number(r.hours) > 0);
    },
    /**
     * 目标类型分布：按 calendarType 统计当月条数。
     * ★ 排除工作日志（type=1）—— 那是每日一条的记录，不是「目标」，
     *   混进来会把分布拉偏。数据来自已加载的 rows，不额外请求接口。
     */
    typeStats() {
      const counts = {};
      this.monthRows
          .filter(r => Number(r.calendarType) !== TYPE_LOG)
          .forEach(r => {
            const t = String(r.calendarType || 3);
            counts[t] = (counts[t] || 0) + 1;
          });
      return Object.keys(CALENDAR_TYPES)
          .filter(t => counts[t] && Number(t) !== TYPE_LOG)
          .map(t => ({type: t, name: CALENDAR_TYPES[t], value: counts[t], color: TYPE_COLORS[t]}));
    },
    /**
     * 本周待办：本周（周一至周日）内未完成的目标。
     * ★ 同样只筛已加载的 rows —— 若这一周跨月，另一头的几天不在本月数据里，
     *   沿用「只看当前月」的既有口径，不额外请求。
     */
    weekTodos() {
      const {start, end} = this.weekRange();
      return this.monthRows
          .filter(r => Number(r.calendarType) !== TYPE_LOG && r.kpiStatus !== 100)
          .filter(r => {
            const d = this.dateOnly(r.goalDate);
            return d >= start && d <= end;
          })
          .sort((a, b) => this.dateOnly(a.goalDate).localeCompare(this.dateOnly(b.goalDate)));
    },
    gridCells() {
      const cells = [];
      if (!this.month) return cells;
      const [y, m] = this.month.split("-").map(Number);
      const firstDow = new Date(y, m - 1, 1).getDay();
      const totalDays = new Date(y, m, 0).getDate();
      const todayStr = this.today();
      for (let i = 0; i < firstDow; i++) cells.push(null);
      for (let d = 1; d <= totalDays; d++) {
        const date = this.month + "-" + String(d).padStart(2, "0");
        const dayRows = this.monthRows.filter(r => this.dateOnly(r.goalDate) === date);
        const goals = dayRows.filter(r => Number(r.calendarType) !== TYPE_LOG);
        const hasLog = dayRows.some(r => Number(r.calendarType) === TYPE_LOG);
        const holiday = this.holidayMap[date];
        cells.push({
          date,
          day: d,
          isToday: date === todayStr,
          // 任务数提示：有任务蓝色、全部完成绿色（模板里按 allDone 切换）
          taskCount: goals.length,
          allDone: goals.length > 0 && goals.every(g => g.kpiStatus === 100),
          // 往日没填日志且是工作日 → 标红。
          // ★ 工作日不等于「非周末」：调休的周六要上班（该标红），
          //   法定假期里的周三不上班（不该标红），都得看假期表。
          overdue: date < todayStr && !hasLog && this.isWorkday(date),
          // 右下角的假期标识：放假显示节日名（没有名称就显示「休」），调休显示「调休」
          holidayText: holiday ? (Number(holiday.dayType) === 1 ? (holiday.holidayName || '休') : '调休') : '',
          holidayType: holiday ? (Number(holiday.dayType) === 1 ? 'off' : 'work') : '',
        });
      }
      return cells;
    },
  },
  beforeRouteLeave(to, from, next) {
    // 将当前页面数据进行缓存
    // Util.cachePageData(this, to, from);
    next();
  },
  async created() {
    let userInfo = await Util.getUserInfo();
    this.userInfo = userInfo || {};
    // 待办跳过来时带 employeeId（可能是管理员查看其他职员的日程）；否则默认当前登录人
    this.searchInfo.employeeId = this.route.query.employeeId || userInfo.id;
    // 默认看今天；带 date 参数（从待办/消息推送点进来的）则直接落到那一天
    this.selectedDate = this.routeDate || this.today();
    // 选中的日期不在本月时把月份跟过去，否则打开的页面停在别的月份、看不到这一天
    if (this.routeDate) {
      this.month = this.routeDate.slice(0, 7);
    }
    this.initStaff();
    this.loadMonth();
  },
  unmounted() {
    if (this.typeChart) {
      this.typeChart.dispose();
    }
  },
  methods: {
    staffLabel(e) {
      const role = e.roleName ? " · " + e.roleName : "";
      return (e.employeeName || e.employeeId) + role;
    },
    typeName(t) {
      return CALENDAR_TYPES[String(t)] || "其他";
    },
    /** 是不是日程（拜访客户/线上会议/线下会议）。日程不显示延期，也不能编辑 KPI任务 */
    isSchedule(item) {
      return SCHEDULE_TYPES.includes(Number(item.calendarType));
    },
    today() {
      const d = new Date();
      return d.getFullYear() + "-" + String(d.getMonth() + 1).padStart(2, "0") + "-" + String(d.getDate()).padStart(2, "0");
    },
    dateOnly(v) {
      return v ? String(v).slice(0, 10) : "";
    },
    /** 格子悬浮提示：让它自己说明为什么标红 */
    dayTip(cell) {
      const parts = [];
      if (cell.holidayText) {
        parts.push(cell.holidayType === "off" ? "放假：" + cell.holidayText : "调休上班");
      }
      if (cell.taskCount) {
        parts.push(cell.taskCount + " 条目标" + (cell.allDone ? "（全部完成）" : ""));
      }
      if (cell.overdue) {
        parts.push("未填工作日志");
      }
      return parts.join(" · ");
    },
    /**
     * 某天是不是工作日
     *
     * 假期表里有记录就听它；没有记录说明既不是法定假期也不是调休日，按普通周末判断。
     * 与后端 CalendarServiceImpl#isWorkday 是同一套口径 —— 两边算出来不一样的话，
     * 会出现「日历标红但延期没跳过」这种对不上的情况。
     */
    isWorkday(date) {
      const holiday = this.holidayMap[date];
      if (holiday) {
        return Number(holiday.dayType) === 2;
      }
      const d = new Date(date + "T00:00:00");
      return d.getDay() !== 0 && d.getDay() !== 6;
    },
    /** 本周一 ~ 本周日 */
    weekRange() {
      const now = new Date();
      // getDay() 里周日是 0，换算成「距周一几天」
      const offset = (now.getDay() + 6) % 7;
      const monday = new Date(now.getFullYear(), now.getMonth(), now.getDate() - offset);
      const sunday = new Date(monday.getFullYear(), monday.getMonth(), monday.getDate() + 6);
      const fmt = d => d.getFullYear() + "-" + String(d.getMonth() + 1).padStart(2, "0") + "-" + String(d.getDate()).padStart(2, "0");
      return {start: fmt(monday), end: fmt(sunday)};
    },
    shortDate(v) {
      const d = this.dateOnly(v);
      return d ? d.slice(5) : "";
    },
    toThisMonth() {
      const now = new Date();
      this.month = now.getFullYear() + "-" + String(now.getMonth() + 1).padStart(2, "0");
      this.loadMonth();
    },
    initStaff() {
      calendarApi.queryEmployeeStaff({}).then(list => {
        this.staffOptions = list || [];
      });
    },
    loadMonth() {
      // 假期是商户级的，与看谁的日历无关，切员工也要重新拉
      this.loadHoliday();
      if (!this.searchInfo.employeeId) {
        this.rows = [];
        this.syncDayInfo();
        return;
      }
      calendarApi.queryMonth({employeeId: this.searchInfo.employeeId, month: this.month}).then(list => {
        this.rows = list || [];
        // 月份变了，当日工作要跟着刷新（选中的日期多半还在这个月里）
        this.syncDayInfo();
        this.$nextTick(() => this.renderTypeChart());
      });
    },
    /**
     * 拉当前查看年份的放假/调休
     *
     * ★ 一次拉整年而不是整月：日历格子的标红、延期判定都要跨月看，
     *   而一年的假期也就二三十条，一次拉回来放 map 里最省事。
     */
    loadHoliday() {
      const year = this.holidayYear;
      calendarHolidayApi.queryByYear(year).then(list => {
        const map = {};
        (list || []).forEach(h => {
          map[this.dateOnly(h.holidayDate)] = h;
        });
        // 翻年时要把上一年的清掉，否则跨年那几天会用错数据
        const kept = {};
        Object.keys(this.holidayMap).forEach(d => {
          if (!d.startsWith(String(year))) {
            kept[d] = this.holidayMap[d];
          }
        });
        this.holidayMap = Object.assign(kept, map);
      }).catch(() => {});
    },
    // ---------- 放假/调休设置 ----------
    openHolidayForm() {
      // 默认带上当前选中日期，多数场景就是从这天开始
      this.holidayForm = {dayType: 1, name: ""};
      this.holidayRange = this.selectedDate ? [this.selectedDate, this.selectedDate] : [];
      this.showHolidayForm = true;
    },
    async saveHolidaySetting() {
      const dates = this.holidayRangeDates;
      if (!dates.length) {
        this.$message.error("请选择日期区间");
        return;
      }
      // 逐天写入。后端按 (商户, 日期) 的唯一键 upsert，重复设置同一天就是覆盖，
      // 所以这里不需要先去重、也不用担心把已有安排写坏
      for (const date of dates) {
        await calendarHolidayApi.save({
          holidayDate: date,
          dayType: this.holidayForm.dayType,
          holidayName: this.holidayForm.name,
        });
      }
      this.$message.success(`已保存 ${dates.length} 天`);
      this.holidayForm = {dayType: 1, name: ""};
      this.holidayRange = [];
      this.loadHoliday();
    },
    deleteHolidaySetting(h) {
      const date = this.dateOnly(h.holidayDate);
      // 同步来源的删了会被下次同步补回来，提示里说清楚，免得用户以为是没删掉
      const tip = Number(h.sourceFrom) === 1
          ? `确认删除 ${date} 的手动设置？删除后该天按国家安排或周末判断`
          : `确认删除 ${date} 的设置？这是接口同步来的，下次同步会重新写入`;
      this.$confirm(tip, "提示", {type: "warning"})
          .then(() => calendarHolidayApi.delete(date))
          .then(() => {
            this.$message.success("已删除");
            this.loadHoliday();
          })
          .catch(() => {});
    },
    /** 点日历某一天：把当天数据加载到「当日工作」，不再弹框 */
    selectDay(date) {
      if (!date) {
        return;
      }
      this.selectedDate = date;
      this.syncDayInfo();
    },
    /**
     * 把选中日期的数据显示到当日工作区
     *
     * ★ calendarList 做深拷贝：它是可编辑的，直接引用 rows 会在用户改动输入框时
     *   一并改掉日历统计与本周待办依赖的源数据。
     */
    syncDayInfo() {
      const date = this.selectedDate;
      const dayRows = date ? this.monthRows.filter(r => this.dateOnly(r.goalDate) === date) : [];
      const log = dayRows.find(r => Number(r.calendarType) === TYPE_LOG);
      const todos = dayRows.filter(r => Number(r.calendarType) !== TYPE_LOG);
      this.currentCalendarInfo = {
        result: log ? (log.result || "") : "",
        // 写日志的人。日志行本身不存姓名，取该员工的显示名
        logEmployeeName: this.currentStaff ? this.currentStaff.employeeName : "",
        calendarList: JSON.parse(JSON.stringify(todos)),
      };
    },
    /** 类型分布环形图。数据来自 typeStats，与页面上其它地方同一口径 */
    renderTypeChart() {
      const dom = this.$refs.typeChart;
      if (!dom || !this.typeStats.length) {
        return;
      }
      if (!this.typeChart) {
        this.typeChart = markRaw(echarts.init(dom));
      }
      const total = this.typeStats.reduce((s, t) => s + t.value, 0);
      this.typeChart.setOption({
        tooltip: {trigger: "item", appendToBody: true, formatter: "{b}：{c} 条"},
        series: [{
          type: "pie", radius: ["58%", "82%"], center: ["50%", "50%"],
          avoidLabelOverlap: false,
          itemStyle: {borderColor: "#fff", borderWidth: 2},
          label: {
            show: true, position: "center",
            formatter: () => "{v|" + total + "}\n{t|总条数}",
            rich: {
              v: {fontSize: 20, fontWeight: "bold", color: "#303133", lineHeight: 26},
              t: {fontSize: 11, color: "#909399"},
            },
          },
          labelLine: {show: false},
          data: this.typeStats.map(t => ({name: t.name, value: t.value, itemStyle: {color: t.color}})),
        }],
      }, true);
    },
    shiftMonth(delta) {
      const [y, m] = this.month.split("-").map(Number);
      const d = new Date(y, m - 1 + delta, 1);
      this.month = d.getFullYear() + "-" + String(d.getMonth() + 1).padStart(2, "0");
      this.loadMonth();
    },
    // ---------- 新建日程（calendar_type 4/5/6，只落一行） ----------
    openScheduleForm() {
      this.scheduleForm = {
        id: "", employeeId: this.searchInfo.employeeId,
        calendarType: 4, partnerId: "", title: "",
        goalDate: this.selectedDate, remark: "",
      };
      this.showScheduleForm = true;
    },
    /** 从「拜访客户/商机客户」切到别的类型时清掉已选对象，避免带着无关数据提交 */
    onScheduleTypeChange(type) {
      if (type !== TYPE_VISIT && type !== TYPE_OPPORTUNITY) {
        this.scheduleForm.partnerId = "";
      }
    },
    async saveSchedule() {
      if (!this.scheduleForm.calendarType) {
        this.$message.error("请选择日程类型");
        return;
      }
      if (this.scheduleForm.calendarType === TYPE_VISIT && !this.scheduleForm.partnerId) {
        this.$message.error("请选择要拜访的客户");
        return;
      }
      if (this.scheduleForm.calendarType === TYPE_OPPORTUNITY && !this.scheduleForm.partnerId) {
        this.$message.error("请选择商机客户");
        return;
      }
      if (!this.scheduleForm.title) {
        this.$message.error("请填写日程标题");
        return;
      }
      if (!this.scheduleForm.goalDate) {
        this.$message.error("请选择日程日期");
        return;
      }
      await calendarApi.saveSchedule(this.scheduleForm);
      this.$message.success("日程已保存");
      this.showScheduleForm = false;
      // 存完跳到该日程所在的那天，用户能立刻看到结果
      this.selectedDate = this.scheduleForm.goalDate;
      this.loadMonth();
    },

    // ---------- 新建 / 编辑 KPI任务 ----------
    openTaskForm() {
      this.taskForm = {
        id: "", title: "", employeeId: this.searchInfo.employeeId,
        startDate: this.selectedDate, endDate: this.selectedDate,
        remark: "", shiftOtherTask: true,
      };
      this.showTaskForm = true;
    },
    /**
     * 点行内 .kpi-edit：编辑这条日历任务自己
     *
     * ★ 不再反查它所属的 kpi任务。原来那样点一下就把整条任务的起止日期拉出来改，
     *   保存时还会重新拆分全程 —— 改某一天的标题会顺手重排整个任务，副作用太大。
     *   现在只把当前这一行的名称与备注填进表单。
     */
    openRowEdit(item) {
      if (!item.id) {
        return;
      }
      this.rowForm = {
        id: item.id,
        title: item.title || "",
        remark: item.remark || "",
      };
      this.showRowForm = true;
    },
    async saveRowEdit() {
      if (!this.rowForm.title) {
        this.$message.error("请填写名称");
        return;
      }
      await calendarApi.saveCalendarRow(this.rowForm);
      this.$message.success("已保存");
      this.showRowForm = false;
      // 重新拉当月：列表与日历格子都依赖 rows，改完标题要一起刷新
      this.loadMonth();
    },
    async saveTask() {
      if (!this.taskForm.title) {
        this.$message.error("请填写任务标题");
        return;
      }
      if (!this.taskForm.startDate || !this.taskForm.endDate) {
        this.$message.error("请选择开始/结束日期");
        return;
      }
      if (this.taskForm.endDate < this.taskForm.startDate) {
        this.$message.error("结束日期不能早于开始日期");
        return;
      }
      await calendarApi.save(this.taskForm);
      this.$message.success("日程已保存并拆分");
      this.showTaskForm = false;
      // loadMonth 里会一并刷新当日工作区
      this.loadMonth();
    },
    // ---------- 目标行：改动即保存 ----------

    /**
     * 状态变更（勾选框 / 下拉）
     *
     * 「未完成/延期」要先二次确认 —— 它会把该任务当天及之后的行整体顺延一天，
     * 是个有副作用的操作，误点一下就把排期改乱了。
     */
    onStatusChange(item, status) {
      if (!this.canEdit) {
        return;
      }
      if (status === item.kpiStatus) {
        return;
      }
      // ★ 已是已完成的任务不允许再改状态。界面上下拉已经禁用了，这里再兜一道：
      //   防止将来有人给下拉加个别的入口、或者禁用态被绕过。
      //   注意只挡状态，备注与工时不受影响 —— 完成之后补记工时是常态。
      if (item.kpiStatus === 100) {
        this.$message.warning("已完成的任务不能再改状态");
        return;
      }
      if (status === 50) {
        this.$confirm("标记为「未完成/延期」后，该任务将整体顺延到下一个工作日，确认吗？", "提示",
            {type: "warning"})
            .then(() => this.saveRow(item, 50, {delay: true}))
            .catch(() => {});
        return;
      }
      this.saveRow(item, status);
    },

    /** 备注 / 工时改动即保存（失焦或回车触发，不会每敲一个字就发请求） */
    onFieldChange(item) {
      if (!this.canEdit) {
        return;
      }
      this.saveRow(item, item.kpiStatus);
    },

    /**
     * 保存单个目标行
     *
     * <p>走的是原来的 markDone 接口：它按 calendarIds 只同步被勾选的那一行，
     * 所以传单个 id 就是「只存这一行」。日志文本传空，避免顺带把日志覆盖掉。
     *
     * @param item   目标行
     * @param status 既要保存的状态
     * @param opts   delay=true 表示这是延期（后端会顺延后续行）
     */
    async saveRow(item, status, opts) {
      const delay = opts && opts.delay;
      const row = Object.assign({}, item, {kpiStatus: status});
      const payload = {
        employeeId: this.searchInfo.employeeId,
        goalDate: this.selectedDate,
        // 不带日志文本：日志由「写日志」弹框单独维护
        result: "",
        calendarIds: [row.id],
        calendarList: [row],
      };
      try {
        await calendarApi.markDone(payload);
        this.$message.success(delay ? "已标记延期并顺延到下一个工作日" : "已保存");
        // 延期会改动后续多天，必须整月重载；普通状态变更只刷新当天即可
        this.loadMonth();
      } catch (e) {
        // 保存失败要把界面改回去，否则界面与库里不一致
        this.syncDayInfo();
      }
    },

    /** 删除单条日程（只有日程可在这里删，KPI 任务走编辑） */
    deleteSchedule(item) {
      this.$confirm(`确认删除日程「${item.title}」？`, "提示", {type: "warning"})
          .then(() => calendarApi.delete(item.id))
          .then(() => {
            this.$message.success("日程已删除");
            this.loadMonth();
          })
          .catch(() => {});
    },

    // ---------- 写工作日志 ----------

    openLogForm() {
      if (!this.canWriteLog) {
        this.$message.warning("只能填写本人的工作日志");
        return;
      }
      this.logForm = {result: this.currentCalendarInfo.result || ""};
      this.showLogForm = true;
    },

    /**
     * 拼装日志：把当天「已完成」或「消耗工时 > 0」的目标拼成文本
     *
     * 覆盖而不是追加 —— 这个按钮的语义是「照当前进度生成一份」，追加会让用户
     * 连点两次就得到重复内容。用户想补充可以自己再编辑文本框。
     */
    assembleLog() {
      const lines = this.logSuggestRows.map(r => {
        // 标题用「：」引出后面的状态；状态之间用顿号，读起来比一串冒号顺
        const marks = [];
        if (r.kpiStatus === 100) {
          marks.push("已完成");
        }
        if (Number(r.hours) > 0) {
          marks.push("消耗工时 " + r.hours + "h");
        }
        return marks.length ? r.title + "：" + marks.join("、") : r.title;
      });
      if (!lines.length) {
        this.$message.warning("当天没有可拼装的目标");
        return;
      }
      this.logForm.result = lines.join("\n");
    },

    async saveLog() {
      if (!this.logForm.result) {
        this.$message.error("请填写日志内容");
        return;
      }
      await calendarApi.markDone({
        employeeId: this.searchInfo.employeeId,
        goalDate: this.selectedDate,
        result: this.logForm.result,
        // 不传 calendarIds：本次只写日志，不同步任何目标行
        calendarIds: [],
        calendarList: [],
      });
      this.$message.success("日志已保存");
      this.showLogForm = false;
      this.loadMonth();
    }
  },
};
</script>

<style lang="scss" scoped>
.calendar-view {
  height: 100%;
}

/* ============ 布局：左主区 + 右侧栏（参考原型「日程安排」） ============ */
.cal-layout {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 280px;
  gap: 12px;
  //padding: 10px;
  box-sizing: border-box;
  // 撑满 list-table 给的高度，让「当日工作」能吃到剩余空间
  height: 100%;

  // 窄屏时侧栏掉到下面，避免主区被挤扁
  @media (max-width: 1200px) {
    grid-template-columns: minmax(0, 1fr);
    height: auto;
  }
}
.cal-main {
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-width: 0;
  height: 100%;
  min-height: 0;
}
.cal-side {
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-width: 0;
  height: 100%;
  overflow: auto;
}

.cal-card,
.sch-card,
.side-card {
  border: 1px solid var(--yq-border-color, #ebeef5);
  border-radius: 6px;
  background: #fff;
}
// 日历按内容占高，剩下的一律留给「当日工作」
.cal-card {
  flex: none;
}
.side-card {
  flex: none;
}

/* ============ 日历 ============ */
.cal-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 14px 0;
}
.cal-title {
  font-size: 14px;
  font-weight: 600;
  color: #303133;
}
.cal-nav {
  display: flex;
  align-items: center;
  gap: 2px;
  .nav-btn {
    min-width: 22px;
    height: 22px;
    padding: 0 4px;
    border-radius: 5px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    color: #909399;
    font-size: 13px;
    user-select: none;
    &:hover {
      background: #f2f6ff;
      color: var(--el-color-primary);
    }
  }
}
.cal-legend {
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
  font-size: 11.5px;
  color: #909399;
  padding: 8px 14px 0;
  .dot {
    display: inline-block;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    margin-right: 4px;
    vertical-align: middle;
    &.today {background: var(--el-color-primary);}
    &.task {background: #409eff;}
    &.done {background: #67c23a;}
    &.overdue {background: #f56c6c;}
  }
}
.cal-grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 4px;
  padding: 10px 14px 14px;

  .cal-dow {
    text-align: center;
    font-size: 11.5px;
    color: #909399;
    padding: 4px 0 6px;
  }
  .cal-blank {
    min-height: 50px;
  }
  // 日历格子只放「日期 + 任务数」，不再罗列任务，所以做得比原来扁
  .cal-day {
    min-height: 50px;
    border: 1px solid var(--yq-border-color, #ebeef5);
    border-radius: 7px;
    padding: 5px 7px;
    cursor: pointer;
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 4px;
    transition: .14s;
    // ★ 必须显式定位：右下角的假期标识是 position:absolute，
    //   少了这行它就会去找更外层的定位祖先，跑到格子外面去
    position: relative;

    &:hover {
      border-color: var(--el-color-primary);
      background: #f7faff;
    }
    // 日期数字：平时黑，往日漏填日志标红
    .cal-date {
      font-size: 12.5px;
      font-weight: 600;
      color: #303133;
    }
    &.overdue .cal-date {color: #f56c6c;}

    // 任务数：有任务蓝、全部完成绿
    .cal-count {
      font-size: 10px;
      line-height: 15px;
      min-width: 15px;
      text-align: center;
      border-radius: 8px;
      padding: 0 4px;
      color: #fff;
      flex: none;
      &.blue {background: #409eff;}
      &.green {background: #67c23a;}
    }
    // 放假/调休标识：右下角。放假红、调休橙，与「漏填日志」的红区分开靠位置与文案
    .cal-holiday {
      position: absolute;
      right: 4px;
      bottom: 3px;
      max-width: calc(100% - 8px);
      font-size: 10px;
      line-height: 14px;
      padding: 0 4px;
      border-radius: 3px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      &.off {color: #f56c6c; background: #fef0f0;}
      &.work {color: #e6a23c; background: #fdf6ec;}
    }

    // 今天：实心主色，与原型一致
    &.today {
      background: var(--el-color-primary);
      border-color: var(--el-color-primary);
      box-shadow: 0 3px 10px rgba(22, 119, 255, .35);
      .cal-date {color: #fff;}
      .cal-count {
        background: rgba(255, 255, 255, .3) !important;
        color: #fff !important;
      }
    }
    // 当前选中的那天：加一圈描边，和「今天」区分开
    &.selected:not(.today) {
      border-color: var(--el-color-primary);
      box-shadow: inset 0 0 0 1px var(--el-color-primary);
    }
  }
}

/* ============ 当日工作 ============ */
.sch-card {
  flex: 1;
  min-height: 220px;
  display: flex;
  flex-direction: column;
}
.sch-card-head {
  flex: none;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 10px 14px;
  border-bottom: 1px solid #f2f5f9;
  .sch-card-title {font-size: 13px; font-weight: 600; color: #303133;}
  .sch-card-actions {display: inline-flex; align-items: center; gap: 8px;}
  // 假期设置：浅灰图标，悬停才变主色，不跟左边的按钮抢注意力
  .holiday-setting {
    font-size: 16px;
    color: #c0c4cc;
    cursor: pointer;
    &:hover {color: var(--el-color-primary);}
  }
}
/* 放假/调休设置弹框 */
.holiday-add {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  padding-bottom: 12px;
  border-bottom: 1px solid #f2f5f9;
  margin-bottom: 10px;
  .range-hint {
    font-size: 12px;
    color: #909399;
  }
}
.holiday-list {
  max-height: 320px;
  overflow: auto;
  .holiday-row {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 7px 0;
    border-bottom: 1px solid #f7f8fa;
    font-size: 12.5px;
    &:last-child {border-bottom: 0;}
    .h-date {flex: 0 0 96px; color: #303133; font-variant-numeric: tabular-nums;}
    .h-type {
      flex: none;
      font-size: 11px;
      line-height: 18px;
      padding: 0 6px;
      border-radius: 3px;
      &.off {color: #f56c6c; background: #fef0f0;}
      &.work {color: #e6a23c; background: #fdf6ec;}
    }
    .h-name {flex: 1; min-width: 0; color: #606266; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;}
    .h-source {
      flex: none;
      font-size: 11px;
      line-height: 18px;
      padding: 0 6px;
      border-radius: 3px;
      // 手动：强调色，提示「这条不会被同步覆盖」
      &.manual {color: #1677ff; background: #ecf5ff;}
      &.sync {color: #909399; background: #f4f4f5;}
    }
    .h-del {flex: none; color: #f56c6c; cursor: pointer; font-size: 14px; &:hover {opacity: .75;}}
  }
}
// 内容多时在本模块内部滚动，不把整页顶长
.sch-body {
  flex: 1;
  min-height: 0;
  overflow: auto;
  padding: 12px 14px 14px;
}

.log-block {
  margin-bottom: 14px;
  .log-label {
    font-size: 12px;
    font-weight: 600;
    color: #606266;
    margin-bottom: 6px;
    .desc {font-weight: 400; color: #c0c4cc;}
  }
  // 只读展示：保留换行，长文本不换行截断
  .log-text {
    font-size: 12.5px;
    color: #303133;
    line-height: 1.7;
    white-space: pre-wrap;
    background: #fafbfc;
    border: 1px solid var(--yq-border-color, #ebeef5);
    border-radius: 4px;
    padding: 8px 10px;
  }
  .log-empty {
    font-size: 12px;
    color: #c0c4cc;
    padding: 8px 0;
  }
}
.task-block {
  .task-label {
    font-size: 12px;
    font-weight: 600;
    color: #606266;
    margin-bottom: 8px;
    .readonly-hint {
      margin-left: 6px;
      font-weight: 400;
      color: #909399;
    }
  }
}
/* 目标行：两行排版 —— 上行是「勾选 分类 名称 状态」，下行是「状态 备注 工时」 */
.task-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.task-row {
  border: 1px solid var(--yq-border-color, #ebeef5);
  border-radius: 6px;
  padding: 8px 10px;
  background: #fff;

  // 只读（看别人的日历）：整行灰一点，示意「这里动不了」。
  // 不用 disabled 是因为它会把「已完成」的绿色也一起压灰，反而看不出状态
  &.task-row-readonly {
    background: #fafbfc;
    .task-name {color: #909399;}
  }

  // 已完成：去掉勾选框之后，靠底色 + 标题划线来表达「这条办完了」
  &.task-row-done {
    background: #f6fbf3;
    border-color: #d6ebc8;
    .task-name {
      color: #909399;
      text-decoration: line-through;
    }
  }

  .task-line {
    display: flex;
    align-items: center;
    gap: 10px;
    min-width: 0;

    & + .task-line {
      margin-top: 8px;
    }
    // 名称与备注都按内容占宽、可收缩：两者合计超一行时各自省略，不会互相挤没。
    // 剩下的空间由 .task-actions 的 margin-left:auto 吃掉，图标因此恒在行尾
    .task-name {
      flex: 0 1 auto;
      min-width: 0;
      font-size: 12.8px;
      color: #303133;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    // 备注：接在名称后面，单行不换行（长了省略，完整内容看 title 悬浮）
    .task-remark {
      flex: 0 1 auto;
      min-width: 0;
      font-size: 12px;
      color: #909399;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    // 操作图标贴右：margin-left:auto 吃掉名称/备注之后的全部剩余空间
    .task-actions {
      flex: none;
      margin-left: auto;
      display: inline-flex;
      align-items: center;
      gap: 8px;
    }
    // .status-done 只用在「写日志」弹框的可拼装列表里，那里是纯文本展示；
    // 任务行里已改成状态下拉，不再用这个类
    .status-done {
      flex: none;
      font-size: 12px;
      color: #67c23a;
      font-weight: 600;
    }
    .task-del {
      flex: none;
      cursor: pointer;
      color: #f56c6c;
      font-size: 14px;
      &:hover {opacity: .75;}
    }
    .status-select {flex: none; width: 122px;}
    .task-result {flex: 1; min-width: 120px;}
    .kpi-hours {
      flex: none;
      display: flex;
      align-items: center;
      gap: 6px;
      .hours-label {font-size: 12px; color: var(--yq-font-color-desc);}
    }
    .kpi-edit {
      flex: none;
      width: 16px;
      cursor: pointer;
      color: var(--el-color-primary);
      font-size: 14px;
    }
  }
}
/* 类型标签：日程与 KPI 任务混在一天里，标出各自是什么 */
.type-tag {
  flex: none;
  font-size: 11px;
  line-height: 18px;
  padding: 0 6px;
  border-radius: 3px;
  background: #f0f2f5;
  color: #606266;
  &.type-2 {background: #ecf5ff; color: #1677ff;}
  &.type-3 {background: #fdf6ec; color: #faad14;}
  &.type-4 {background: #e8f7f7; color: #13b8b8;}
  &.type-5 {background: #f0edfe; color: #7a5af8;}
  &.type-6 {background: #eaf6ef; color: #22a55a;}
  &.type-7 {background: #fdeef7; color: #eb2f96;}
  &.type-8 {background: #fff2e8; color: #d4380d;}
  &.type-99 {background: #f4f4f5; color: #909399;}
}
/* 写日志弹框里「可拼装」的提示列表 */
.log-suggest {
  margin-top: 12px;
  .log-suggest-title {
    font-size: 12px;
    font-weight: 600;
    color: #606266;
    margin-bottom: 8px;
  }
  .log-suggest-list {max-height: 200px; overflow: auto;}
  .log-suggest-row {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 6px 0;
    border-bottom: 1px solid #f7f8fa;
    font-size: 12px;
    &:last-child {border-bottom: 0;}
    .sug-title {
      flex: 1;
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      color: #303133;
    }
    .sug-hours {flex: none; color: #909399;}
  }
}
.sch-empty {
  padding: 18px 0;
  font-size: 12px;
  color: #909399;
  text-align: center;
}

/* ============ 侧栏 ============ */
.side-card {padding: 12px 14px 14px;}
.side-title {
  font-size: 13px;
  font-weight: 600;
  color: #303133;
  margin-bottom: 8px;
}
.side-chart {height: 160px; width: 100%;}
.side-legend {
  margin-top: 8px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  .legend-row {
    display: flex;
    align-items: center;
    font-size: 12px;
    color: #606266;
    .dot {
      display: inline-block;
      width: 8px;
      height: 8px;
      border-radius: 50%;
      margin-right: 6px;
    }
    .legend-name {flex: 1;}
    .legend-val {color: #303133;}
  }
}
.week-list {
  display: flex;
  flex-direction: column;
  .week-item {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 7px 0;
    border-bottom: 1px solid #f7f8fa;
    cursor: pointer;
    font-size: 12px;
    &:last-child {border-bottom: 0;}
    &:hover .week-title {color: var(--el-color-primary);}
    .week-date {
      flex: 0 0 40px;
      color: #909399;
      font-variant-numeric: tabular-nums;
    }
    .week-title {
      flex: 1;
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      color: #303133;
    }
  }
}

</style>
